import { useEffect, useState } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import { getExistingAcceptances, ExistingTermsAcceptances } from "../../data-functions/terms-acceptance-api";
import { TERMS_DOCUMENTS, TermsDocumentType } from "../../types/terms-acceptance";

interface ApplicantTermsAcceptanceSummaryProps {
  applicantId: string;
  title?: string;
}

const DOCUMENT_ORDER: TermsDocumentType[] = ["gwp_terms", "hops_privacy_policy"];

export const ApplicantTermsAcceptanceSummary = ({
  applicantId,
  title = "Terms acceptance",
}: ApplicantTermsAcceptanceSummaryProps) => {
  const [acceptances, setAcceptances] = useState<ExistingTermsAcceptances>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!applicantId) {
      setAcceptances({});
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const loadAcceptances = async () => {
      setIsLoading(true);

      try {
        const loadedAcceptances = await getExistingAcceptances(applicantId);
        if (isActive) {
          setAcceptances(loadedAcceptances);
        }
      } catch (error) {
        console.error("Failed to load terms acceptances", error);
        if (isActive) {
          setAcceptances({});
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadAcceptances();

    return () => {
      isActive = false;
    };
  }, [applicantId]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="button" sx={{ fontWeight: "bold" }}>
          {title}:
        </Typography>
      </Grid>
      {isLoading ? (
        <Grid item xs={12}>
          <Typography color="text.secondary">Loading terms acceptance status...</Typography>
        </Grid>
      ) : (
        DOCUMENT_ORDER.map((documentType) => {
          const acceptance = acceptances[documentType];

          return (
            <Grid item xs={12} md={6} key={documentType}>
              <Stack spacing={0.75}>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  {TERMS_DOCUMENTS[documentType].title}:
                </Typography>
                <Typography>{acceptance ? "Accepted" : "Not accepted"}</Typography>
                <Typography color="text.secondary">
                  {acceptance
                    ? `Accepted on ${new Date(acceptance.acceptedAt).toLocaleString()}`
                    : "No acceptance recorded"}
                </Typography>
                {acceptance && (
                  <>
                    <Typography color="text.secondary">
                      Language: {acceptance.language.toUpperCase()}
                    </Typography>
                    <Typography color="text.secondary">
                      Version: {acceptance.documentVersion}
                    </Typography>
                    {documentType === "gwp_terms" && (
                      <Typography color="text.secondary">
                        Optional new services notification:{" "}
                        {acceptance.optionalNewServicesAccepted ? "yes" : "no"}
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
          );
        })
      )}
    </Grid>
  );
};
